import { useEffect, useMemo, useState } from "react";
import cn from "classnames";
import { SeaReferenceTypesModel } from "entities/shared/seaCard.types";
import { HiddentInputCoordinate } from "features/shared/OpenedCardEvents/types";
import { cardToDeleteStore } from "features/CardToDelete";
import { ChangeShippingLine } from "features/ChangeShippingLine";
import { EditTags } from "features/EditTags";
import { filteredCardsStore } from "features/Filter";
import { openedCardStore } from "features/OpenedCard";
import { useSeaUrlQueryHandler } from "features/QueryParamsManager";
import { createSeaUpdateCardHook, useSeaCardActions } from "features/SeaCardActions";
import { useSeaOpenedCardIsFollowButtonVisible } from "features/SeaCardActions/model/hooks/useSeaOpenedCardIsFollowButtonVisible";
import { ContainerList, Demurrage, EventList, /* Exceptions, */ VesselList } from "features/SeaOpeneCardTabs";
import { useSeaSearch } from "features/Search";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { useTagsContext } from "features/Tags";
import { tagsStore } from "features/Tags";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { Card, CardBase } from "entities/Card";
import { searchFilterStore } from "entities/SearchFilter";
import { isSeaSavedShipmentModel } from "entities/SeaSavedCard";
import { createSeaSearchQueryParams, isSeaSearchShipmentModel, useOpenedCardSearchQuery } from "entities/SeaSearchCard";
import { subscriptionStore } from "entities/Subscription";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Button, ButtonIcon } from "shared/ui/Button";
import { Preloader } from "shared/ui/Preloader";
import { Tabs } from "shared/ui/Tabs";
import { Tooltip } from "shared/ui/Tooltip";
import { defaultActiveTabMap, TabIdList } from "./constants";
// import { useOpenedCardRatesTrigger } from "./hooks/useOpenedCardRatesTrigger";
import s from "./OpenedCardView.module.scss";

type SeaOpenedCardLayerProps = {
  className?: string;
};

export const SeaOpenedCardView: RFC<SeaOpenedCardLayerProps> = ({ className }) => {
  // useOpenedCardRatesTrigger();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { refreshTags } = useTagsContext();
  const { isCurrentViewOpenedCard, currentView, clearViewHistory, showMainView } = usePanelViews();
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const cardsByTag = useAppSelector(tagsStore.selectors.cardsByTags);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const { allTags, activeTag, setActiveTag, getCardsByTags } = useTagsContext();

  const seaCardsByTag = useMemo(() => {
    return cardsByTag.filter((card) => isSeaSavedShipmentModel(card));
  }, [cardsByTag.length]);

  const seaOpenedCard = useMemo(() => {
    if (isSeaSavedShipmentModel(openedCard)) return openedCard;
    if (isSeaSearchShipmentModel(openedCard)) return openedCard;
    return null;
  }, [openedCard]);

  const seaSavedCard = useMemo(() => {
    if (isSeaSavedShipmentModel(openedCard)) return openedCard;
    return null;
  }, [openedCard]);

  useSeaUrlQueryHandler({
    openedCard: seaOpenedCard,
  });

  const {
    searchRequest,
    searchResponse: cardData,
    isSearchLoading,
    isSearchFetching,
    setSearchCache,
    resetSearchQuery,
    cancelSearchRequest,
  } = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);
  const isCardShippingLineVisible = useAppSelector(appStore.selectors.isCardShippingLineVisible);
  const isCardSearchVisible = useAppSelector(appStore.selectors.isCardSearchVisible);
  const { isSeaFollowButtonVisible } = useSeaOpenedCardIsFollowButtonVisible({ openedCard: seaOpenedCard });

  const { deleteSeaCardHandler, seaCardFollow, isSeaCardFollowLoading, isSeaCardDeleting } = useSeaCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const searchRequestMeta = useSeaSearch({
    isAuthenticated,
  });

  const { updateCard, shouldUpdateCard, isResolvingUpdatePossibility } = createSeaUpdateCardHook({
    model: isSeaSavedShipmentModel(seaOpenedCard) ? seaOpenedCard : null,
    isOpenedCardExist: !!seaOpenedCard,
    openedCardUpdateAction: (response) => ({
      type: openedCardStore.actions.updateOpenCardAction.type,
      payload: response,
    }),
    updateFilteredCards: (response) => ({
      type: filteredCardsStore.actions.updateFilterCardAction.type,
      payload: response,
    }),
    updateCardByTagAction: (response) => ({
      type: tagsStore.actions.updateCardByTagAction.type,
      payload: response,
    }),
  })();

  const [tooltipLinkName, setTooltipLinkName] = useState(t("Delete"));
  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    if (!requestedFromUrl) return;

    if (isSeaSearchShipmentModel(seaOpenedCard) && seaOpenedCard?.rawData) {
      setSearchCache(seaOpenedCard.rawData);
    }
  }, [seaOpenedCard?.rawData]);

  useEffect(() => {
    if (!isCurrentViewOpenedCard || isSearchLoading || isSearchFetching) {
      return;
    }

    if (isSeaSearchShipmentModel(seaOpenedCard)) {
      setSearchCache(seaOpenedCard.rawData);
      return;
    }

    if (isSeaSavedShipmentModel(seaOpenedCard)) {
      const reqParams = createSeaSearchQueryParams({ model: seaOpenedCard, requestType: "openedCard" });
      if (!reqParams) return;

      searchRequest(reqParams);
    }
  }, [currentView, seaOpenedCard?.rawData]);

  useEffect(() => {
    if (cardData?.requestLimitReached) {
      clearViewHistory();
      showMainView();
      dispatch(openedCardStore.actions.setOpenedCardAction(null));
    }
  }, [cardData?.rawData]);

  useEffect(
    () => () => {
      resetSearchQuery();
      dispatch(openedCardStore.actions.setOpenedCardAction(null));
      cancelSearchRequest();
    },
    []
  );

  const shouldRenderContainersTab = useMemo(
    () =>
      cardData?.referenceType &&
      [SeaReferenceTypesModel.BK, SeaReferenceTypesModel.BL].includes(cardData?.referenceType),
    [cardData?.referenceType]
  );

  const initialTabId = useMemo<TabIdList>(() => {
    const referenceType = cardData?.referenceType;
    if (!referenceType) {
      return TabIdList.routes;
    }
    return (
      defaultActiveTabMap[referenceType.toUpperCase() as ValueOf<typeof SeaReferenceTypesModel>] ?? TabIdList.routes
    );
  }, [cardData?.referenceType]);

  const onUpdateHandler = () => {
    return isSeaSavedShipmentModel(seaOpenedCard) ? updateCard(seaOpenedCard) : undefined;
  };

  const shouldUpdateCardHandler = (): boolean => {
    return isSeaSavedShipmentModel(seaOpenedCard) ? shouldUpdateCard() : false;
  };

  const onFollowHandler = () => {
    if (isSeaCardFollowLoading) return;
    if (isSeaSearchShipmentModel(seaOpenedCard)) {
      seaCardFollow(seaOpenedCard);
    }
  };

  const onDeleteHandler = () => {
    if (!seaOpenedCard) return;
    deleteSeaCardHandler(seaOpenedCard);
  };

  const copyUrlHandler = () => {
    const currentURL = window.location.href;
    navigator.clipboard.writeText(currentURL).then(() => {
      setTooltipLinkName(t("Copied"));
    });
  };

  const polCoordinate = useMemo<HiddentInputCoordinate | null>(() => {
    const pol = cardData?.polCoordinate;
    if (!pol) return null;
    return [pol.lat, pol.lng];
  }, [cardData?.rawData]);

  const podCoordinate = useMemo<HiddentInputCoordinate | null>(() => {
    const pod = cardData?.podCoordinate;
    if (!pod) return null;
    return [pod.lat, pod.lng];
  }, [cardData?.rawData]);

  if (isSearchLoading || isSearchFetching || searchRequestMeta.isSeaSearchLoading) {
    return <Preloader className={cn(s.loader, className)} />;
  }

  return (
    <>
      {polCoordinate && <input type="hidden" name="coord_from" value={JSON.stringify(polCoordinate)} />}
      {podCoordinate && <input type="hidden" name="coord_to" value={JSON.stringify(podCoordinate)} />}

      <div className={s.cardWrapper}>
        <Card
          shipmentType={shipmentType}
          className={s.openedCardShipmentCard}
          ata={cardData?.ata}
          atd={cardData?.atd}
          eta={cardData?.eta}
          etd={cardData?.etd}
          predictiveETA={cardData?.predictiveETA}
          referenceType={cardData?.referenceType ?? null}
          containersAmount={cardData?.containersAmount ?? 0}
          reference={cardData?.reference}
          status={cardData?.status ?? null}
          directionFrom={cardData?.fromDirection}
          directionTo={cardData?.toDirection}
          progressLine={{ status: "location-data", percent: cardData?.progress ?? 0, rotation: "horizontal" }}
          canUpdate={shouldUpdateCardHandler()}
          isUpdating={isResolvingUpdatePossibility}
          onDelete={isCardSearchVisible ? onDeleteHandler : undefined}
          onUpdate={onUpdateHandler}
          onFollow={isSeaFollowButtonVisible && isAuthenticated ? onFollowHandler : undefined}
          isFollowLoading={isSeaCardFollowLoading}
          containersSizeTypes={cardData?.containersSizeTypes ?? []}
          scac={cardData?.getCarrierScac() ?? null}
          carrierName={cardData?.carrierName}
          tags={seaSavedCard?.tags}
        />
      </div>

      <div className={cn(s.cardsWrapper, s.cardWrapper)}>
        <CardBase
          className={cn(s.openedCard, className)}
          onUpdate={onUpdateHandler}
          canUpdate={shouldUpdateCardHandler()}
          isUpdating={isResolvingUpdatePossibility}
        >
          <CardBase.Row className={s.tabsRow}>
            <Tabs className={s.tabs} initialTabId={initialTabId} onChangeActiveTab={setActiveTab}>
              <Tabs.Row className={s.tabsControls}>
                <Tabs.Group>
                  <Tabs.Control
                    dataFor={TabIdList.routes}
                    data-test-id={`openedCard-${TabIdList.routes}-tab-${openedCard?.reference}`}
                  >
                    <Tabs.ControlLabel>{t("Route")}</Tabs.ControlLabel>
                  </Tabs.Control>
                  <Tabs.Control
                    dataFor={TabIdList.vessels}
                    data-test-id={`openedCard-${TabIdList.vessels}-tab-${openedCard?.reference}`}
                  >
                    <Tabs.ControlLabel>{t("Vessel")}</Tabs.ControlLabel>
                  </Tabs.Control>
                  {shouldRenderContainersTab && (
                    <Tabs.Control
                      dataFor={TabIdList.containers}
                      data-test-id={`openedCard-${TabIdList.containers}-tab-${openedCard?.reference}`}
                    >
                      <Tabs.ControlLabel>{t("Containers")}</Tabs.ControlLabel>
                    </Tabs.Control>
                  )}
                  {/* TODO: add exceptions tab */}
                  {/* <Tabs.Control
                    dataFor={TabIdList.exceptions}
                    data-test-id={`openedCard-${TabIdList.exceptions}-tab-${openedCard?.reference}`}
                  >
                    <Tabs.ControlLabel>{t("Exceptions")}</Tabs.ControlLabel>
                  </Tabs.Control> */}
                  {/* TODO: add demurrage tab */}
                  {/* <Tabs.Control
                    dataFor={TabIdList.demurrage}
                    data-test-id={`openedCard-${TabIdList.demurrage}-tab-${openedCard?.reference}`}
                  >
                    <Tabs.ControlLabel>{t("Demurrage")}</Tabs.ControlLabel>
                  </Tabs.Control> */}
                </Tabs.Group>
              </Tabs.Row>
              <Tabs.Body className={s.tabsBody}>
                <Tabs.Panel data-tab={TabIdList.routes} className={cn(s.panel, s.routesPanel)}>
                  <EventList data={cardData} />
                </Tabs.Panel>
                <Tabs.Panel data-tab={TabIdList.vessels} className={cn(s.panel, s.vesselsPanel)}>
                  <VesselList data={cardData} />
                </Tabs.Panel>
                {shouldRenderContainersTab && (
                  <Tabs.Panel data-tab={TabIdList.containers} className={s.panel}>
                    <ContainerList data={cardData} />
                  </Tabs.Panel>
                )}
                {/* <Tabs.Panel data-tab={TabIdList.exceptions} className={s.panel}>
                  <Exceptions data={cardData} />
                </Tabs.Panel> */}
                <Tabs.Panel data-tab={TabIdList.demurrage} className={s.panel}>
                  <Demurrage data={cardData} />
                </Tabs.Panel>
              </Tabs.Body>
            </Tabs>
          </CardBase.Row>

          {isCardShippingLineVisible && activeTab === TabIdList.routes && (
            <CardBase.Row className={s.changeShippingLineRow}>
              <ChangeShippingLine
                openedCard={seaOpenedCard}
                setOpenedCardAction={openedCardStore.actions.setOpenedCardAction}
                updateOpenCardAction={openedCardStore.actions.updateOpenCardAction}
              />
            </CardBase.Row>
          )}

          <CardBase.Row className={s.bottomActions}>
            {isAuthenticated && (
              <EditTags
                openedCard={openedCard}
                cardsByTag={seaCardsByTag}
                allTags={allTags}
                setCardsByTagAction={(cards) => ({
                  type: tagsStore.actions.setCardsByTagAction.type,
                  payload: cards,
                })}
                refreshTags={refreshTags}
                getCardsByTags={getCardsByTags}
                activeTag={activeTag}
                setActiveTag={setActiveTag}
              />
            )}
            <div className={s.actionsContainer}>
              {isSeaFollowButtonVisible && (
                <Button
                  disabled={isSeaCardFollowLoading}
                  variant="pale"
                  size="tiny"
                  className={s.follow}
                  onClick={onFollowHandler}
                  data-test-id={`card-action-follow-${openedCard?.reference}`}
                >
                  {t("Follow")}
                </Button>
              )}

              {isCardSearchVisible && (
                <Tooltip
                  triggerElement={
                    <ButtonIcon
                      disabled={isSeaCardDeleting}
                      icon="trash"
                      className={s.delete}
                      onClick={onDeleteHandler}
                      data-test-id={`openedCard-action-delete-${openedCard?.reference}`}
                    />
                  }
                  offsetY={5}
                >
                  <p className={s.tooltipButtonsText}>{t("Delete")}</p>
                </Tooltip>
              )}

              <Tooltip
                triggerElement={
                  <ButtonIcon
                    icon="link"
                    onClick={copyUrlHandler}
                    className={s.linkButton}
                    onMouseEnter={() => setTooltipLinkName(t("Copy"))}
                    data-test-id={`openedCard-action-copy-${openedCard?.reference}`}
                  />
                }
                offsetY={5}
              >
                <p className={s.tooltipButtonsText}>{tooltipLinkName}</p>
              </Tooltip>
            </div>
          </CardBase.Row>
        </CardBase>
      </div>
    </>
  );
};

SeaOpenedCardView.displayName = SeaOpenedCardView.name;
